import { Injectable } from '@angular/core';
import { toLabelArray, undefOrEmpty } from '@app/shared/functions/utils';
import { SelectionOption } from '@app/shared/interfaces/selection-option';
import { Subscription } from 'rxjs';
import { CustomerOrderService } from './customer-order.service';

export interface EnquiryBasicData {
  enquiryNum: string;
  enquiryDate: string;
  deliveryAddress: string;
  deliveryPincode: string;
}

export interface QuotationData {
  quotationNum: string;
  quotationPdfUrl: string;
  quotationMessage: string;
}

export interface InvoiceData {
  invoiceNum: string;
  customerInvoiceUrl: string;
  invoiceStatus: SelectionOption;
}

export interface OrderItemMobile {
  itemDescription: string;
  itemQty: string;
}

interface PersonData {
  name: string;
  email: string;
  mobile: string;
}

export interface RelationshipManagerData extends PersonData {
  manager?: PersonData;
}

export interface OrderItemData {
  category: string;
  product: string;
  make: string;
  grade: string;
  brand: string;
  type: string;
  property: string;
  dimension: string;
  customDimensions: string;
  qty: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerOrderDataService {
  private customerOrderData: { [key: string]: any } = {};

  private customerOrderDataStruct: { [key: string]: any } = {};

  queryOrder!: Subscription;

  orderId!: string;
  isInternalUser!: boolean;

  constructor(private customerOrderService: CustomerOrderService) {}

  public cleanUp() {
    this.queryOrder?.unsubscribe();
  }

  runService(orderId: string, isInternalUser: boolean): Promise<void> {
    this.orderId = orderId;
    this.isInternalUser = isInternalUser;

    return new Promise((resolve, reject) => {
      this.queryOrder = this.customerOrderService
        .getApiPublicCustomerOrder(this.orderId, this.isInternalUser)
        .subscribe({
          next: (response) => {
            this.customerOrderData = response.data;

            this.setOrderDataIntoStructure();

            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  setOrderDataIntoStructure(): void {
    this.customerOrderDataStruct = {
      customerName: this.customerOrderData['project'].customer.name,
    };

    this.customerOrderDataStruct['enquiryBasicData'] = {
      enquiryNum: this.customerOrderData['enquiryNum'].toUpperCase(),
      enquiryDate: this.customerOrderData['enquiryDate'],
      deliveryAddress: this.customerOrderData['project'].projectLocation,
      deliveryPincode: this.customerOrderData['project'].projectPincode,
    };

    this.customerOrderDataStruct['publicRequirementsId'] = this.customerOrderData['publicId'];

    this.customerOrderDataStruct['relationshipManagerData'] = {
      name: this.customerOrderData['customerOrderOwner'].name,
      email: this.customerOrderData['customerOrderOwner'].email,
      mobile: this.customerOrderData['customerOrderOwner'].mobile,
    };

    let orderItems: OrderItemData[] = [];

    for (let i = 0; i < this.customerOrderData['customerOrderRequirementItemList'].length; i++) {
      const orderItem = this.customerOrderData['customerOrderRequirementItemList'][i];

      orderItems.push({
        category: undefOrEmpty(orderItem['materialCategory']?.label),
        product: undefOrEmpty(orderItem['materialProduct']?.label),
        make: toLabelArray(orderItem['materialMakeList'], 'make'),
        grade: toLabelArray(orderItem['materialGradeList'], 'grade'),
        brand: toLabelArray(orderItem['materialBrandList'], 'brand'),
        type: undefOrEmpty(orderItem['materialType']?.label),
        property: undefOrEmpty(orderItem['materialProperty']?.label),
        dimension: undefOrEmpty(orderItem['materialDimension']?.label),
        customDimensions: orderItem['materialCustomDimensions'],
        qty: orderItem['qty'] + ' ' + orderItem['qtyUom'].value,
      });
    }

    this.customerOrderDataStruct['orderItemList'] = orderItems;

    let quotationList: QuotationData[] = [];

    for (let i = 0; i < this.customerOrderData['quotationListPublic'].length; i++) {
      const quotation = this.customerOrderData['quotationListPublic'][i];

      let message: string = '';

      if (quotation['customerPoNum'] && quotation['customerPoDate']) {
        message = 'Accepted with PO No. ' + quotation['customerPoNum'] + ' on ' + quotation['customerPoDate'];
      }

      quotationList.push({
        quotationNum: quotation['quotationNum'],
        quotationPdfUrl: quotation['quotationPdfPublicFileUrl'],
        quotationMessage: message,
      });
    }

    this.customerOrderDataStruct['quotationList'] = quotationList;

    let invoiceList: InvoiceData[] = [];

    for (let i = 0; i < this.customerOrderData['customerInvoiceListPublic'].length; i++) {
      const invoice = this.customerOrderData['customerInvoiceListPublic'][i];

      invoiceList.push({
        invoiceNum: invoice['invoiceNum'],
        customerInvoiceUrl: invoice['customerInvoicePdfPublicFileUrl'],
        invoiceStatus: invoice['invoiceDeliveryStatus'],
      });
    }

    this.customerOrderDataStruct['invoiceList'] = invoiceList;
  }

  getOrderData(): { [key: string]: any } {
    return this.customerOrderData;
  }

  getOrderResponseDataString() {
    let something: string = JSON.stringify(this.customerOrderData, null, 2);
    something = something.replace(/(?<=[a-z])([A-Z])/g, '_$1').toLowerCase();

    return something;
  }

  getOrderDataStructured(): { [key: string]: any } {
    return this.customerOrderDataStruct;
  }
}
